import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ApplicationForm } from './onboarding/ApplicationForm';
import { ApplicationHub } from './onboarding/ApplicationHub';
import { DecisionPolicy } from './onboarding/DecisionPolicy';
import { DecisionReport } from './onboarding/DecisionReport';
import { DecisionAction } from './onboarding/DecisionAction';
import { MonitorDashboard } from './onboarding/MonitorDashboard';
const DESKTOP_WIDTH = 960;
const DESKTOP_HEIGHT = 580;
const TABS = [{
  id: 'onboarding',
  label: 'Onboard',
  scenes: [{
    id: 0,
    component: ApplicationForm
  }, {
    id: 1,
    component: ApplicationHub
  }]
}, {
  id: 'decision',
  label: 'Decision',
  scenes: [{
    id: 0,
    component: DecisionPolicy
  }, {
    id: 1,
    component: DecisionAction
  }]
}, {
  id: 'monitor',
  label: 'Monitor',
  scenes: [{
    id: 0,
    component: MonitorDashboard
  }, {
    id: 1,
    component: DecisionReport
  }]
}];
export function ProductShowcase() {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [currentScene, setCurrentScene] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [cycleCount, setCycleCount] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const manualTabChangeRef = useRef(false);
  const currentTab = TABS.find(t => t.id === activeTab)!;
  const scenes = currentTab.scenes;
  const hasScenes = scenes.length > 0;
  useEffect(() => {
    setCurrentScene(0);
  }, [activeTab]);
  // Responsive scaling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const updateScale = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth < DESKTOP_WIDTH) {
        setScale(containerWidth / DESKTOP_WIDTH);
      } else {
        setScale(1);
      }
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const CurrentComponent = hasScenes ? scenes[currentScene]?.component : null;
  const handleSceneComplete = useCallback(() => {
    // Don't auto-advance if user manually changed tabs
    if (manualTabChangeRef.current) {
      return;
    }
    if (currentScene < scenes.length - 1) {
      setCurrentScene(prev => prev + 1);
    } else {
      const currentTabIndex = TABS.findIndex(t => t.id === activeTab);
      let nextTabIndex = currentTabIndex + 1;
      while (nextTabIndex < TABS.length && TABS[nextTabIndex].scenes.length === 0) {
        nextTabIndex++;
      }
      if (nextTabIndex < TABS.length) {
        setActiveTab(TABS[nextTabIndex].id);
      } else {
        setCycleCount(prev => prev + 1);
        setActiveTab(TABS[0].id);
      }
    }
  }, [currentScene, scenes.length, activeTab]);
  const needsScale = scale < 1;
  const scaledHeight = DESKTOP_HEIGHT * scale;
  return <div className="w-full max-w-6xl mx-auto flex flex-col" data-id="element-947">
      {/* Scene area */}
      <div ref={containerRef} className="relative flex flex-col" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-948">
        {/* Scene container with responsive scaling */}
        <div className="relative w-full mx-auto overflow-hidden rounded-2xl" style={{
        height: needsScale ? scaledHeight : DESKTOP_HEIGHT
      }} data-id="element-949">
          <div className="absolute top-0 left-0" style={{
          width: needsScale ? DESKTOP_WIDTH : '100%',
          height: DESKTOP_HEIGHT,
          transform: needsScale ? `scale(${scale})` : undefined,
          transformOrigin: 'top left'
        }} data-id="element-950">
            <AnimatePresence initial={false} mode="sync" data-id="element-951">
              {CurrentComponent && <motion.div key={`${activeTab}-${currentScene}-${cycleCount}`} initial={{
              opacity: 0,
              scale: 0.98,
              zIndex: 0
            }} animate={{
              opacity: 1,
              scale: 1,
              zIndex: 10
            }} exit={{
              opacity: 0,
              scale: 0.98,
              zIndex: 0
            }} transition={{
              duration: 0.15,
              ease: 'easeInOut'
            }} className="absolute inset-0" style={{
              zIndex: 10
            }} onAnimationComplete={definition => {
              if (definition === 'exit') {
                // Force cleanup after exit animation
              }
            }} data-id="element-952">
                  <div className="w-full h-full rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/80 overflow-hidden bg-white" data-id="element-953">
                    <CurrentComponent onComplete={handleSceneComplete} data-id="element-954" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Navigation - overlapping the scene edge */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[55%] z-50 flex flex-col items-center gap-2" data-id="element-955">
          {/* Tab pills */}
          <div className="flex gap-1 p-1.5 bg-white/90 rounded-full border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.10),0_1px_4px_rgba(0,0,0,0.06)] backdrop-blur-md max-[479px]:gap-0.5 max-[479px]:p-1" data-id="element-956">
            {TABS.map(tab => {
            const isActive = tab.id === activeTab;
            const hasContent = tab.scenes.length > 0;
            return <button key={tab.id} onClick={() => {
              if (!hasContent) return;
              if (tab.id === activeTab) {
                // Restart the current tab
                setCurrentScene(0);
                setCycleCount(prev => prev + 1);
              } else {
                // Switch to a different tab
                manualTabChangeRef.current = true;
                setActiveTab(tab.id);
                setCurrentScene(0);
                setCycleCount(prev => prev + 1);
                setTimeout(() => {
                  manualTabChangeRef.current = false;
                }, 100);
              }
            }} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 max-[479px]:px-3 max-[479px]:py-1.5 max-[479px]:text-xs
                    ${isActive ? 'bg-white text-[#6C5CE7] shadow-[0_2px_10px_rgba(108,92,231,0.15)] border border-[#6C5CE7]/20 ring-1 ring-[#6C5CE7]/20' : hasContent ? 'text-gray-600 bg-gray-50 border border-gray-200/80 hover:text-gray-900 hover:bg-gray-100 hover:border-gray-300 cursor-pointer shadow-sm' : 'text-gray-400 bg-gray-50/50 border border-gray-200/40 cursor-not-allowed'}`} disabled={!hasContent} data-id="element-957">
                  {tab.label}
                </button>;
          })}
          </div>

          {/* Dot indicators below tabs */}
          {scenes.length > 1 && <div className="flex items-center justify-center gap-2.5" data-id="element-958">
              {scenes.map((scene, i) => <button key={scene.id} onClick={() => setCurrentScene(i)} className={`h-2 rounded-full transition-all duration-300 ${currentScene === i ? 'w-8 bg-[#6C5CE7]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to scene ${i + 1}`} data-id="element-959" />)}
            </div>}
        </div>
      </div>

      {/* Spacer for the overhanging tab bar */}
      <div className="h-10" data-id="element-960" />
    </div>;
}